import { useState, useCallback } from 'react';
import { validateForm, hasFormErrors } from '../utils/validation';

/**
 * Custom hook for form validation
 * @param {object} initialData - Initial form data
 * @param {object} validationRules - Validation rules object
 * @returns {object} - Form validation utilities
 */
export const useFormValidation = (initialData = {}, validationRules = {}) => {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Update form data
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const updateField = useCallback((field, value) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  }, [errors]);

  /**
   * Update multiple fields at once
   * @param {object} updates - Object with field updates
   */
  const updateFields = useCallback((updates) => {
    setData(prev => ({
      ...prev,
      ...updates
    }));

    // Clear errors for updated fields
    const updatedFields = Object.keys(updates);
    if (updatedFields.some(field => errors[field])) {
      setErrors(prev => {
        const newErrors = { ...prev };
        updatedFields.forEach(field => {
          if (newErrors[field]) {
            newErrors[field] = null;
          }
        });
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Mark field as touched
   * @param {string} field - Field name
   */
  const touchField = useCallback((field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  }, []);

  /**
   * Validate a specific field
   * @param {string} field - Field name
   * @returns {string|null} - Error message or null
   */
  const validateField = useCallback((field) => {
    if (!validationRules[field]) return null;

    const fieldRules = validationRules[field];
    const value = data[field];

    for (const rule of fieldRules) {
      const error = rule(value, data);
      if (error) {
        return error;
      }
    }

    return null;
  }, [data, validationRules]);

  /**
   * Validate all form fields
   * @returns {object} - Validation errors object
   */
  const validateAllFields = useCallback(() => {
    const validationErrors = validateForm(data, validationRules);
    setErrors(validationErrors);
    return validationErrors;
  }, [data, validationRules]);

  /**
   * Handle field blur event
   * @param {string} field - Field name
   */
  const handleBlur = useCallback((field) => {
    touchField(field);
    
    const error = validateField(field);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  }, [touchField, validateField]);

  /**
   * Handle form submission
   * @param {function} onSubmit - Submit handler function
   * @returns {function} - Form submit handler
   */
  const handleSubmit = useCallback((onSubmit) => {
    return async (event) => {
      if (event) {
        event.preventDefault();
      }

      setIsSubmitting(true);

      // Mark all fields as touched
      const allFields = Object.keys(validationRules);
      const touchedFields = {};
      allFields.forEach(field => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields);

      // Validate all fields
      const validationErrors = validateAllFields();

      if (!hasFormErrors(validationErrors)) {
        try {
          await onSubmit(data);
        } catch (error) {
          console.error('Form submission error:', error);
        }
      }

      setIsSubmitting(false);
    };
  }, [data, validationRules, validateAllFields]);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setData(initialData);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialData]);

  /**
   * Set form data (useful for editing existing records)
   * @param {object} newData - New form data
   */
  const setFormData = useCallback((newData) => {
    setData(newData);
    setErrors({});
    setTouched({});
  }, []);

  /**
   * Get field props for form inputs
   * @param {string} field - Field name
   * @returns {object} - Props object for form input
   */
  const getFieldProps = useCallback((field) => {
    return {
      value: data[field] || '',
      onChange: (event) => {
        const value = event.target.type === 'checkbox' 
          ? event.target.checked 
          : event.target.value;
        updateField(field, value);
      },
      onBlur: () => handleBlur(field),
      error: touched[field] && !!errors[field],
      helperText: touched[field] ? errors[field] : ''
    };
  }, [data, errors, touched, updateField, handleBlur]);

  /**
   * Get field error (only if field is touched)
   * @param {string} field - Field name
   * @returns {string|null} - Error message or null
   */
  const getFieldError = useCallback((field) => {
    return touched[field] ? errors[field] : null;
  }, [errors, touched]);

  /**
   * Check if field has error
   * @param {string} field - Field name
   * @returns {boolean} - True if field has error
   */
  const hasFieldError = useCallback((field) => {
    return touched[field] && !!errors[field];
  }, [errors, touched]);

  /**
   * Check if form is valid
   * @returns {boolean} - True if form is valid
   */
  const isFormValid = useCallback(() => {
    const validationErrors = validateForm(data, validationRules);
    return !hasFormErrors(validationErrors);
  }, [data, validationRules]);

  return {
    // Form data
    data,
    errors,
    touched,
    isSubmitting,

    // Field operations
    updateField,
    updateFields,
    touchField,
    validateField,
    validateAllFields,

    // Event handlers
    handleBlur,
    handleSubmit,

    // Form operations
    resetForm,
    setFormData,

    // Helper functions
    getFieldProps,
    getFieldError,
    hasFieldError,
    isFormValid
  };
};